import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { microservice } from '@/configs';
import { getToken } from '@/configs/coalEduAuth';
import { handleResponseStatus } from '@/logic/handleResponseStatus';

declare module 'axios' {
  export interface AxiosRequestConfig {
    payload?: {
      notifySuccess?: boolean;
      notifyFail?: boolean;
    };
    jsonp?: boolean;
    jsonpCallback?: string;
  }
}

// 服务前缀列表
const services = microservice.serverList.map((item) => item.apiPrefix);
type ServiceName = (typeof services)[number];

export interface JsonpOptions {
  callback?: string; // 自定义回调函数名
  callbackParam?: string; // URL 中 callback 参数的名称，默认 'callback'
  timeout?: number; // 超时时间，默认 10000ms
  forceScript?: boolean; // 强制使用 script 标签，不尝试 XHR（默认 false）
}

/**
 * JSONP 请求（参考 jQuery 实现）
 * 智能选择请求方式：
 * 1. 优先尝试 XHR（如果服务器支持 CORS）
 * 2. 如果 XHR 失败，自动降级到 script 标签方式
 */
function jsonpRequest(url: string, options?: JsonpOptions | string): Promise<any> {
  // 兼容旧的字符串参数方式
  const opts: JsonpOptions = typeof options === 'string' ? { callback: options } : options || {};
  const callbackParam = opts.callbackParam || 'callback';
  const timeout = opts.timeout || 10000;
  const forceScript = opts.forceScript || false;

  // 如果强制使用 script 或者明确指定了回调函数名，直接使用 script 方式
  if (forceScript || opts.callback) {
    return jsonpScriptRequest(url, opts, callbackParam, timeout);
  }

  // 智能模式：先尝试 XHR，失败后降级到 script（参考 jQuery）
  return jsonpXhrRequest(url, callbackParam, timeout).catch(() => {
    // XHR 失败（可能是 CORS 问题），降级到 script 方式
    return jsonpScriptRequest(url, opts, callbackParam, timeout);
  });
}

/**
 * 使用 XHR 方式发送 JSONP 请求（适用于支持 CORS 的服务器）
 */
function jsonpXhrRequest(url: string, callbackParam: string, timeout: number): Promise<any> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isCompleted = false;

    // 生成回调函数名用于 URL 参数（服务器需要知道这是 JSONP 请求）
    const callbackName = `jsonp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      isCompleted = true;
    };

    // 设置超时
    if (timeout > 0) {
      timeoutId = setTimeout(() => {
        if (!isCompleted) {
          xhr.abort();
          cleanup();
          reject(new Error('XHR request timeout'));
        }
      }, timeout);
    }

    xhr.onload = () => {
      if (isCompleted) return;
      cleanup();

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          let data;
          const responseText = xhr.responseText.trim();

          // 尝试解析 JSONP 格式：callback({"data": ...})
          const jsonpMatch = responseText.match(/^[^(]*\(([\s\S]*)\);?$/);
          if (jsonpMatch) {
            // 是 JSONP 格式，提取括号内的 JSON
            data = JSON.parse(jsonpMatch[1]);
          } else {
            // 纯 JSON 格式
            data = JSON.parse(responseText);
          }

          resolve(data);
        } catch (e) {
          reject(new Error('Invalid JSON response'));
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      if (isCompleted) return;
      cleanup();
      reject(new Error('XHR request failed'));
    };

    xhr.onabort = () => {
      if (isCompleted) return;
      cleanup();
      reject(new Error('XHR request aborted'));
    };

    try {
      // 添加 callback 参数（服务器需要这个参数来识别 JSONP 请求）
      const separator = url.includes('?') ? '&' : '?';
      const requestUrl = `${url}${separator}${callbackParam}=${callbackName}`;

      xhr.open('GET', requestUrl, true);
      xhr.withCredentials = true; // 支持跨域携带 cookie
      xhr.setRequestHeader('Accept', 'application/json, text/javascript, */*');
      xhr.send();
    } catch (e) {
      cleanup();
      reject(e);
    }
  });
}

/**
 * 使用 script 标签方式发送 JSONP 请求（适用于不支持 CORS 的服务器）
 */
function jsonpScriptRequest(
  url: string,
  opts: JsonpOptions,
  callbackParam: string,
  timeout: number,
): Promise<any> {
  return new Promise((resolve, reject) => {
    // 生成唯一的回调函数名（jQuery 风格）
    const callbackName =
      opts.callback || `jsonp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const script = document.createElement('script');
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isAborted = false;

    const cleanup = () => {
      // 清理定时器
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // 移除 script 标签
      if (script.parentNode) {
        try {
          script.parentNode.removeChild(script);
        } catch (e) {
          // 忽略移除错误
        }
      }

      // 删除全局回调函数
      try {
        delete (window as any)[callbackName];
      } catch (e) {
        // IE8 下可能需要设置为 undefined
        (window as any)[callbackName] = undefined;
      }
    };

    // 定义全局回调函数（参考 jQuery 实现）
    (window as any)[callbackName] = (data: any) => {
      if (isAborted) return;
      cleanup();
      resolve(data);
    };

    // 设置超时（参考 jQuery 的 timeout 参数）
    if (timeout > 0) {
      timeoutId = setTimeout(() => {
        if (isAborted) return;
        isAborted = true;
        cleanup();
        reject(new Error('JSONP request timeout'));
      }, timeout);
    }

    // 处理脚本加载错误（参考 jQuery 的错误处理）
    script.onerror = () => {
      if (isAborted) return;
      isAborted = true;
      cleanup();
      reject(new Error('JSONP request failed'));
    };

    // 构造 URL（参考 jQuery 的 dataType: 'jsonp'）
    const separator = url.includes('?') ? '&' : '?';
    script.src = `${url}${separator}${callbackParam}=${callbackName}`;
    script.type = 'text/javascript';
    script.async = true; // 异步加载，jQuery 也是这样
    script.charset = 'utf-8';

    // 添加到 DOM（参考 jQuery，添加到 head）
    const head = document.head || document.getElementsByTagName('head')[0];
    head.appendChild(script);
  });
}

const isDEV = location.host.includes('localhost');
function createServiceInstance(prefix?: string): AxiosInstance {
  const instance = axios.create({
    baseURL: prefix ? (prefix == 'json' ? `/${prefix}` : `/${prefix}/front`) : undefined,
  });

  instance.interceptors.request.use((config) => {
    if (isDEV && getToken() && getToken() !== 'undefined') {
      config.headers['coaledutoken'] = getToken();
    }
    return config;
  });
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const { status, data } = response || {};
      if (status !== 200) {
        switch (status) {
          case 401:
            // 未登录或登录失效
            break;
          case 403:
            // 没有权限
            break;
          default:
          // 其他错误
        }
        return Promise.reject(response.data);
      }
      return data;
    },
    (error) => {
      if (error?.response) {
        const { status, data } = error.response;
        const responseData =
          typeof data === 'object' && data !== null
            ? (data as { message?: string; notLogin?: number })
            : undefined;
        if (
          !isDEV &&
          !handleResponseStatus(status, responseData?.message, responseData?.notLogin)
        ) {
          return;
        }
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

const defaultInstance = createServiceInstance();

const customAxios = defaultInstance as typeof defaultInstance & Record<ServiceName, AxiosInstance>;
services.forEach((service) => {
  customAxios[service] = createServiceInstance(service);
});

const extendedRequest = Object.assign(customAxios, {
  jsonp: (url: string, options?: JsonpOptions | string) => {
    return jsonpRequest(url, options);
  },
  jsonpWithConfig: (config: { url: string } & JsonpOptions) => {
    const { url, ...options } = config;
    return jsonpRequest(url, options);
  },
});

export interface IRequestCommonResponse<T = null> {
  status: 0 | 1;
  message: string;
  data: T;
}

export default extendedRequest;
